<template>
  <div
    class="handoff-backdrop"
    :class="{ 'theme-ecruise': $store.state.UI === 'ecruise' }"
    @keydown.esc.stop.prevent="$emit('cancel')"
  >
    <section
      ref="dialog"
      class="handoff-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="handoff-title"
      tabindex="-1"
    >
      <div class="handoff-header">
        <span>Crew View</span>
        <h2 id="handoff-title">Finalize Order</h2>
      </div>

      <div class="handoff-body">
        <div class="handoff-row">
          <span>Subtotal</span>
          <span>${{ subtotal.toFixed(2) }}</span>
        </div>

        <div class="handoff-row">
          <span>Discounts</span>
          <span>{{ discounts > 0 ? `-$${discounts.toFixed(2)}` : '$0.00' }}</span>
        </div>

        <div class="handoff-row">
          <span>Tax</span>
          <span>${{ Number(order.tax_amount ?? 0).toFixed(2) }}</span>
        </div>

        <div class="handoff-row">
          <span>
            Auto Gratuity{{ order.auto_gratuity_percent
              ? ` (${order.auto_gratuity_percent}%)`
              : '' }}
          </span>
          <span>${{ Number(order.auto_gratuity_amount ?? 0).toFixed(2) }}</span>
        </div>

        <div v-if="Number(order.gratuity_amount) > 0" class="handoff-row">
          <span>Additional Tip</span>
          <span>${{ Number(order.gratuity_amount).toFixed(2) }}</span>
        </div>

        <div class="handoff-row handoff-total">
          <strong>Total Payable</strong>
          <strong>
            ${{ Number(order.total ?? order.subtotal ?? 0).toFixed(2) }}
          </strong>
        </div>

        <div class="handoff-payment">
          <span>Payment Method</span>
          <strong>{{ paymentLabel }}</strong>
        </div>
      </div>

      <div class="handoff-note">
        <span>✍️ Digital signature enabled</span>
        <p>
          Pressing <strong>YES</strong> hands off to the guest for additional
          tip &amp; signature.
        </p>
      </div>

      <div class="handoff-actions">
        <button type="button" @click="$emit('cancel')">NO</button>
        <button type="button" @click="$emit('confirm')">→ &nbsp;YES</button>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'GuestSignatureHandoffModal',

  props: {
    order: { type: Object, required: true },
    payers: { type: Array, required: true },
  },

  emits: ['cancel', 'confirm'],

  computed: {
    subtotal() {
      return Number(
        this.order.original_subtotal ?? this.order.subtotal ?? 0
      );
    },

    discounts() {
      return Math.max(
        0,
        this.subtotal - Number(this.order.subtotal ?? 0)
      ) + Number(this.order.m_discount_amount ?? 0);
    },

    paymentLabel() {
      if (!this.payers.length) return 'Cabin/Crew charge';

      if (this.payers.length > 1) {
        return `Cabin/Crew charge — ${this.payers.length} payers`;
      }

      const payer = this.payers[0];
      const name = [payer.first_name, payer.last_name]
        .filter(Boolean)
        .join(' ');
      const cabin = payer.cabin ? `, Cabin ${payer.cabin}` : '';

      return `${payer.person_type === 'C' ? 'Crew' : 'Cabin'} Charge — ${name}${cabin}`;
    },
  },

  mounted() {
    this.$refs.dialog.focus();
  },
};
</script>

<style scoped lang="less">
.handoff-backdrop {
  --accent: #01abc0;
  --cancel: #e86d32;

  position: fixed;
  z-index: 10000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  box-sizing: border-box;
  background: linear-gradient(135deg, #e0f7fa, #b2ebf2 50%, #e0f2f1);
  color: #1c282d;
  font-family: 'OS', sans-serif;

  &.theme-ecruise {
    --accent: #3f8ec5;
    --cancel: #eb262b;
  }
}

.handoff-card {
  width: 100%;
  max-width: 720px;
  margin: auto;
  overflow: hidden;
  border-radius: 16px;
  background: white;
  box-shadow: 0 8px 40px rgba(0, 0, 0, .18);
  outline: none;
}

.handoff-header {
  padding: 22px 28px;
  background: var(--accent);
  color: white;
  text-align: center;

  span {
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 2px;
    opacity: .9;
  }

  h2 {
    margin: 3px 0 0;
    font-size: 28px;
    line-height: 1.2;
    font-weight: 700;
  }
}

.handoff-body {
  padding: 24px 32px 0;
}

.handoff-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  color: #465c68;
  font-size: 17px;
}

.handoff-total {
  margin-top: 10px;
  padding-top: 16px;
  border-top: 2px solid #e0e0e0;
  color: #1c282d;
  font-size: 22px;
}

.handoff-payment {
  margin-top: 20px;
  padding: 18px 20px;
  border-radius: 10px;
  background: #f5f9fc;
  text-align: left;

  span {
    display: block;
    margin-bottom: 6px;
    color: #465c68;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  strong {
    display: block;
    font-size: 18px;
    line-height: 1.35;
  }
}

.handoff-note {
  padding: 22px 32px 0;
  color: #465c68;
  font-size: 16px;
  line-height: 1.5;
  text-align: center;

  span {
    display: inline-block;
    padding: 5px 14px;
    border-radius: 20px;
    background: #e8f5e9;
    color: #2e7d32;
    font-size: 14px;
    font-weight: 700;
  }

  p {
    margin: 10px 0 0;
  }
}

.handoff-actions {
  display: flex;
  gap: 16px;
  padding: 28px 32px 30px;

  button {
    flex: 1;
    min-height: 60px;
    border: 2px solid transparent;
    border-radius: 10px;
    font-family: 'OS', sans-serif;
    font-size: 20px;
    font-weight: 700;
    cursor: pointer;
  }

  button:first-child {
    border-color: var(--cancel);
    background: white;
    color: var(--cancel);
  }

  button:last-child {
    background: var(--accent);
    color: white;
  }
}

@media (max-width: 520px) {
  .handoff-backdrop { padding: 12px; }
  .handoff-header { padding: 18px; }
  .handoff-header h2 { font-size: 24px; }
  .handoff-body { padding: 18px 18px 0; }
  .handoff-row { font-size: 15px; }
  .handoff-total { font-size: 19px; }
  .handoff-payment strong { font-size: 16px; }
  .handoff-note { padding: 18px 18px 0; font-size: 14px; }
  .handoff-actions { gap: 10px; padding: 20px 18px; }
  .handoff-actions button { min-height: 54px; font-size: 17px; }
}
</style>